// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get, put, del } from './api';
import { ENDPOINTS } from '../utils/constants';

export const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB
const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB per block

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface StoreObjectAttributes {
  [key: string]: string;
}

export interface StoreObjectResponse {
  data: {
    key: string;
    url: string | null;
    urlExpiry?: number;
    attributes?: Record<string, string>;
    createdAt?: string;
    updatedAt?: string;
    size?: number;
  };
}

// Tracks whether an upload is in progress to enforce one-at-a-time.
let uploadInProgress = false;

/**
 * Upload a file to object store via the BFF, then directly to the SAS URL.
 * Files larger than CHUNK_SIZE are uploaded as Azure Block Blobs (chunked).
 *
 * @param key        Object store key (e.g. "plans/datacenter-1/plan.json")
 * @param file       The File to upload
 * @param attributes Optional metadata attributes for the object
 * @param onProgress Optional progress callback
 * @param signal     Optional AbortSignal for cancellation
 * @param tenantId   Optional tenant sent as X-Tenant-ID on the register call
 *                   (e.g. "helios-onboarding"); the SAS upload itself goes
 *                   straight to Azure and needs no tenant header
 */
export const uploadStoreObject = async (
  key: string,
  file: File,
  attributes: StoreObjectAttributes = {},
  onProgress?: (_progress: UploadProgress) => void,
  signal?: AbortSignal,
  tenantId?: string
): Promise<void> => {
  if (uploadInProgress) {
    throw new Error('An upload is already in progress. Please wait for it to finish.');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File exceeds the maximum allowed size of 500 MB.`);
  }

  uploadInProgress = true;

  try {
    // Step 1: Register the object with the BFF to receive a SAS upload URL.
    const { data } = tenantId
      ? await put<StoreObjectResponse>(ENDPOINTS.OBJECT_STORE_OBJECT(key), attributes, {
          headers: { 'X-Tenant-ID': tenantId },
        })
      : await put<StoreObjectResponse>(ENDPOINTS.OBJECT_STORE_OBJECT(key), attributes);

    if (!data.url) {
      throw new Error('No upload URL returned from storage service.');
    }

    const sasUrl = data.url;

    // Step 2: Upload file to the SAS URL.
    if (file.size <= CHUNK_SIZE) {
      await uploadSimple(sasUrl, file, onProgress, signal);
    } else {
      await uploadChunked(sasUrl, file, onProgress, signal);
    }
  } finally {
    uploadInProgress = false;
  }
};

/** Returns true if an upload is currently in progress. */
export const isUploadInProgress = (): boolean => uploadInProgress;

/** @internal Reset the upload lock. Only for use in tests. */
export const _resetUploadLock = (): void => {
  uploadInProgress = false;
};

/**
 * Delete an object from object store.
 *
 * @param key Object store key (e.g. "plans/datacenter-1/plan.json")
 */
export const deleteStoreObject = (key: string): Promise<{ message: string }> => {
  return del<{ message: string }>(ENDPOINTS.OBJECT_STORE_OBJECT(key));
};

/**
 * Retrieve one of a site's objects. Pass `includeUrl: true` to receive a
 * time-limited SAS download URL in the response.
 *
 * @param siteId     The site the object belongs to; the key must be one of its own
 *                   (`plan_narration/{siteId}/…`, `financial/{siteId}/…`)
 * @param key        Object store key
 * @param includeUrl When true, the response includes `url` and `urlExpiry`
 */
export const getStoreObject = (
  siteId: string,
  key: string,
  includeUrl = false
): Promise<StoreObjectResponse> => {
  const endpoint = includeUrl
    ? `${ENDPOINTS.OBJECT_STORE_SITE_OBJECT(siteId, key)}?includeUrl=true`
    : ENDPOINTS.OBJECT_STORE_SITE_OBJECT(siteId, key);
  return get<StoreObjectResponse>(endpoint);
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function uploadSimple(
  url: string,
  file: File,
  onProgress?: (_p: UploadProgress) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new DOMException('Upload aborted.', 'AbortError'));
      });
    }

    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100),
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.({ loaded: file.size, total: file.size, percentage: 100 });
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}.`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed due to a network error.')));
    xhr.addEventListener('abort', () => reject(new DOMException('Upload aborted.', 'AbortError')));

    xhr.open('PUT', url);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

async function uploadChunked(
  sasUrl: string,
  file: File,
  onProgress?: (_p: UploadProgress) => void,
  signal?: AbortSignal
): Promise<void> {
  const blockIds: string[] = [];
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let bytesUploaded = 0;

  for (let i = 0; i < totalChunks; i++) {
    if (signal?.aborted) {
      throw new DOMException('Upload aborted.', 'AbortError');
    }

    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    // Azure Block Blob block IDs must be base64-encoded and same length for all blocks.
    const blockId = btoa(String(i).padStart(6, '0'));
    blockIds.push(blockId);

    await uploadBlock(sasUrl, chunk, blockId, signal);

    bytesUploaded += chunk.size;
    onProgress?.({
      loaded: bytesUploaded,
      total: file.size,
      percentage: Math.round((bytesUploaded / file.size) * 100),
    });
  }

  await commitBlockList(sasUrl, blockIds, file.type, signal);

  onProgress?.({ loaded: file.size, total: file.size, percentage: 100 });
}

function uploadBlock(
  sasUrl: string,
  chunk: Blob,
  blockId: string,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new DOMException('Upload aborted.', 'AbortError'));
      });
    }

    xhr.addEventListener('load', () => {
      // Azure returns 201 Created for a staged block.
      if (xhr.status === 201) {
        resolve();
      } else {
        reject(new Error(`Block upload failed with status ${xhr.status}.`));
      }
    });

    xhr.addEventListener('error', () =>
      reject(new Error('Block upload failed due to a network error.'))
    );
    xhr.addEventListener('abort', () => reject(new DOMException('Upload aborted.', 'AbortError')));

    const url = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;
    xhr.open('PUT', url);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.send(chunk);
  });
}

function commitBlockList(
  sasUrl: string,
  blockIds: string[],
  contentType: string,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new DOMException('Upload aborted.', 'AbortError'));
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Block list commit failed with status ${xhr.status}.`));
      }
    });

    xhr.addEventListener('error', () =>
      reject(new Error('Block list commit failed due to a network error.'))
    );
    xhr.addEventListener('abort', () => reject(new DOMException('Upload aborted.', 'AbortError')));

    const body = [
      '<?xml version="1.0" encoding="utf-8"?>',
      '<BlockList>',
      ...blockIds.map(id => `  <Latest>${id}</Latest>`),
      '</BlockList>',
    ].join('\n');

    const url = `${sasUrl}&comp=blocklist`;
    xhr.open('PUT', url);
    xhr.setRequestHeader('x-ms-blob-content-type', contentType || 'application/octet-stream');
    xhr.send(body);
  });
}
